import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import StatusChip from "../../../../components/common/StatusChip";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { setTenantId } from "../../../../utils/requestHeaders";
import useAuth from "../../../../hooks/useAuth";
import { getApiErrorMessage } from "../../../../helpers";
import {
  DEFAULT_PAGE_SIZE,
  USER_TYPES,
  USER_TYPE_LABELS,
} from "../../constants";
import { systemUserService, tenantUserService } from "../../services";
import { normalizeUser, normalizeUserList } from "../../utils/normalizeUser";
import {
  detectUserSearchType,
  USER_SEARCH_TYPES,
} from "../../../../helpers/userSearch.helper";

const matchesNationalMobile = (storedMobile, national10) => {
  const storedDigits = String(storedMobile || "").replace(/\D/g, "");
  if (!national10 || !storedDigits) return false;
  return storedDigits === national10 || storedDigits.endsWith(national10);
};

/**
 * IDP stores mobiles with country codes (+91, +1, …) and exact mobile query
 * often fails for 10-digit-only search. Scan tenant pages and match last 10 digits.
 */
const findTenantUsersByNationalMobile = async (
  national10,
  tenantId,
  { pageSize = 50, maxPages = 40 } = {}
) => {
  const matches = [];
  for (let page = 0; page < maxPages; page += 1) {
    const payload = await tenantUserService.getAllTenantUsers({
      size: pageSize,
      index: page,
      tenantId,
    });
    const batch = normalizeUserList(payload);
    if (!batch.length) break;

    batch.forEach((user) => {
      if (matchesNationalMobile(user.mobile, national10)) {
        matches.push(user);
      }
    });

    if (batch.length < pageSize) break;
  }
  return matches;
};

const UserManagement = ({ userType = USER_TYPES.SYSTEM }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenantId: authTenantId, activeTenantId } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const isSystem = userType === USER_TYPES.SYSTEM;
  const listBase = isSystem ? "/systemusers" : "/tenantusers";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [appliedSearch, setAppliedSearch] = useState(null);

  useEffect(() => {
    if (!isSystem && tenantId) {
      setTenantId(tenantId);
    }
  }, [isSystem, tenantId]);

  const runSearch = useCallback(
    async (search, index = 0, size = pageSize) => {
      if (!search) return;
      setLoading(true);
      setError("");
      if (!isSystem && tenantId) {
        setTenantId(tenantId);
      }

      try {
        let list = [];

        if (search.type === USER_SEARCH_TYPES.USER_ID) {
          const detail = isSystem
            ? await systemUserService.getSystemUserById(search.value)
            : await tenantUserService.getTenantUserById(search.value, tenantId);
          const user = normalizeUser(detail || {});
          list = user.userId ? [user] : [];
          setHasMore(false);
        } else if (isSystem) {
          if (search.type === USER_SEARCH_TYPES.USERNAME) {
            try {
              const detail = await systemUserService.getSystemUserByUsername(
                search.value
              );
              const user = normalizeUser(detail || {});
              list = user.userId || user.username ? [user] : [];
            } catch {
              const all = normalizeUserList(
                await systemUserService.getAllSystemUsers()
              );
              const q = search.value.toLowerCase();
              list = all.filter((user) =>
                String(user.username || "")
                  .toLowerCase()
                  .includes(q)
              );
            }
          } else if (search.type === USER_SEARCH_TYPES.EMAIL) {
            const all = normalizeUserList(
              await systemUserService.getAllSystemUsers()
            );
            const q = search.value.toLowerCase();
            list = all.filter((user) =>
              String(user.email || "")
                .toLowerCase()
                .includes(q)
            );
          } else if (search.type === USER_SEARCH_TYPES.MOBILE) {
            const all = normalizeUserList(
              await systemUserService.getAllSystemUsers()
            );
            const national = search.value;
            list = all.filter((user) =>
              matchesNationalMobile(user.mobile, national)
            );
          }
          setHasMore(false);
        } else if (search.type === USER_SEARCH_TYPES.MOBILE) {
          const national = search.value;
          // Try API with 10-digit query first (if backend supports it).
          const payload = await tenantUserService.searchTenantUsers({
            size,
            index: 0,
            tenantId,
            mobile: national,
          });
          let matches = normalizeUserList(payload).filter((user) =>
            matchesNationalMobile(user.mobile, national)
          );

          // Fallback: match last 10 digits against stored values with any country code.
          if (!matches.length) {
            matches = await findTenantUsersByNationalMobile(national, tenantId);
          }

          const start = index * size;
          list = matches.slice(start, start + size);
          setHasMore(start + list.length < matches.length);
        } else {
          const filters = {
            size,
            index,
            tenantId,
          };
          if (search.type === USER_SEARCH_TYPES.USERNAME) {
            filters.username = search.queryValue || search.value;
          } else if (search.type === USER_SEARCH_TYPES.EMAIL) {
            filters.email = search.queryValue || search.value;
          }
          const payload = await tenantUserService.searchTenantUsers(filters);
          list = normalizeUserList(payload);
          setHasMore(list.length >= size);
        }

        setUsers(list);
        setPageIndex(index);
        setPageSize(size);
        setHasSearched(true);
      } catch (err) {
        setUsers([]);
        setHasSearched(true);
        const message = getApiErrorMessage(err);
        setError(message);
        dispatch(appActions.apiErrorMessage(message));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, isSystem, pageSize, tenantId]
  );

  const handleSearch = () => {
    const detected = detectUserSearchType(searchInput);
    if (!detected) {
      setError("Enter a User Name, Email, Mobile, or User ID to search.");
      setUsers([]);
      setHasSearched(false);
      setAppliedSearch(null);
      return;
    }
    setAppliedSearch(detected);
    runSearch(detected, 0, pageSize);
  };

  const handleClear = () => {
    setSearchInput("");
    setAppliedSearch(null);
    setUsers([]);
    setError("");
    setHasSearched(false);
    setPageIndex(0);
  };

  const columns = useMemo(
    () => [
      {
        field: "userId",
        headerName: "User ID",
        flex: 1.2,
        minWidth: 180,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "username",
        headerName: "Username",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "enabled",
        headerName: "Status",
        flex: 0.7,
        minWidth: 110,
        valueGetter: (value, row) => {
          if (row?.locked) return "Locked";
          return row?.enabled ? "Active" : "Disabled";
        },
        renderCell: (params) => (
          <StatusChip enabled={params.row.enabled} locked={params.row.locked} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 88,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ActionIconButton
            title="View Details"
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={() =>
              navigate(`${listBase}/view/${encodeURIComponent(params.row.userId)}`)
            }
          />
        ),
      },
    ],
    [listBase, navigate]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title={USER_TYPE_LABELS[userType] || "Users"}
          subtitle="Search to find users. The full user list is not loaded until you search."
          sx={{ mb: 0 }}
        />
        <Button
          variant="contained"
          startIcon={<PersonAddAltOutlinedIcon />}
          onClick={() => navigate(`${listBase}/create`)}
        >
          Add User
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            label="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            size="small"
            fullWidth
            sx={{ maxWidth: { md: 480 } }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              startIcon={<SearchOutlinedIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
            <Button variant="text" onClick={handleClear} disabled={loading}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 320,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!hasSearched ? (
          <EmptyState
            title="Search for a user"
            description="Enter a User Name, Email, Mobile number, or User ID, then click Search."
          />
        ) : loading && !users.length ? (
          <PageLoader text="Searching users..." />
        ) : !loading && !users.length ? (
          <EmptyState
            title="No users found"
            description="No users matched the current search."
          />
        ) : (
          <DataTable
            rows={users}
            columns={columns}
            pageSize={pageSize}
            showPagination={!isSystem}
            loading={loading}
            autoHeight
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            getRowId={(row) => row.userId || row.id}
            paginationMode={isSystem ? "client" : "server"}
            rowCount={
              isSystem
                ? users.length
                : hasMore
                  ? (pageIndex + 1) * pageSize + 1
                  : pageIndex * pageSize + users.length
            }
            paginationModel={
              isSystem ? undefined : { page: pageIndex, pageSize }
            }
            onPaginationModelChange={
              isSystem || !appliedSearch
                ? undefined
                : ({ page, pageSize: nextSize }) => {
                    runSearch(appliedSearch, page, nextSize);
                  }
            }
          />
        )}
      </Paper>
    </Box>
  );
};

export default UserManagement;
